// serializer
import ReceiptByItemSerializer from "issara-sdk/types/ReceiptByItemSerializer_apps_BIL";
import WalletListSerializer from "issara-sdk/types/WalletListSerializer_apps_BIL";

import formatComma from "react-lib/utils/formatComma";

import type { InvoiceDiscount } from "../common/sequence/BillingV4";
import { coveredOf, payStatusOf, type InvoiceItem } from "./billingModelV4";

// ---------- ยอดที่ต้องชำระ ----------

/** ปัดเป็นทศนิยม 2 ตำแหน่ง */
export const round2 = (value: number) => Math.round(value * 100) / 100;

/** ผลของ BIL/payment-calculate/ */
export type PayCalc = {
  subtotal_price: string;
  total_invoice_discount: string;
  total_voucher_invoice_discount: string;
  round_off: string;
  net_payable: string;
};

export type PayKind = "qr" | "cc" | "dc" | "chq" | "cpn";

export type PaySummary = {
  subtotal: number;
  coverage: number;
  net: number;
  walletTotal: number;
  due: number;
  cash: number;
  others: number;
  kindTotals: Record<PayKind, number>;
  total: number;
  change: number;
  remain: number;
  enough: boolean;
};

/** ค่าใช้จ่ายทั้งหมด / สิทธิ / ต้องชำระ / ยอดที่รับ / เงินทอน / ยอดคงเหลือ */
export const paySummaryOf = (data: {
  items: InvoiceItem[];
  calc?: PayCalc | null;
  walletTotal: number;
  cash: string;
  lines: PayLine[];
}): PaySummary => {
  let subtotal = 0;
  let coverage = 0;

  for (const item of data.items) {
    if (payStatusOf(item) === "pending") {
      subtotal += Number(item.price) || 0;
      coverage += coveredOf(item);
    }
  }

  const net = Number(data.calc?.net_payable) || 0;
  const due = round2(Math.max(0, net - data.walletTotal));
  const cash = Math.max(0, Number(data.cash) || 0);
  const kindTotals: Record<PayKind, number> = { qr: 0, cc: 0, dc: 0, chq: 0, cpn: 0 };
  let others = 0;

  for (const line of data.lines) {
    const amount = Math.max(0, Number(line.amount) || 0);
    kindTotals[line.kind] += amount;
    others += amount;
  }

  others = round2(others);

  const total = round2(cash + others);

  return {
    subtotal,
    coverage,
    net,
    walletTotal: data.walletTotal,
    due,
    cash,
    others,
    kindTotals,
    total,
    change: round2(Math.max(0, total - due)),
    remain: round2(Math.max(0, due - total)),
    enough: due === 0 || total >= due,
  };
};

export type DiscountUnit = "บาท" | "%";

/** ส่วนลดท้ายบิลเป็นบาท (% คิดจากค่าใช้จ่ายทั้งหมด) */
export const discountBahtOf = (unit: DiscountUnit, amount: number, base: number) =>
  round2(unit === "%" ? (base * Math.min(amount, 100)) / 100 : Math.min(amount, base));

/** invoice_discounts ของ payment-calculate / receipt-by-item */
export const invoiceDiscountsOf = (baht: number): InvoiceDiscount[] =>
  baht > 0 ? [{ discount_policy: null, name: "ส่วนลดท้ายใบเสร็จ", value: baht.toFixed(2) }] : [];

/** มูลค่าราคาปกติของรายการที่ตัดสิทธิ์ Package */
export const packageValueOf = (items: InvoiceItem[]) => {
  let value = 0;

  for (const item of items) {
    value += Number(item.unitprice) || 0;
  }

  return value;
};

// ---------- Wallet ----------

export type Wallet = WalletListSerializer;

export type WalletUse = {
  wallet: Wallet;
  use: number;
  warn: string;
};

/** ยอดที่ใช้จากแต่ละ Wallet ตามลำดับในรายการ (ไม่เกินคงเหลือ และไม่เกินยอดที่ต้องชำระ) */
export const walletUsesOf = (
  wallets: Wallet[],
  picked: number[],
  typed: Record<number, string | undefined>,
  net: number,
): WalletUse[] => {
  let left = net;
  const uses: WalletUse[] = [];

  for (const wallet of wallets) {
    if (!wallet.id || !picked.includes(wallet.id)) {
      continue;
    }

    const balance = Number(wallet.balance) || 0;
    const text = typed[wallet.id];
    const isTyped = text !== undefined && text !== "";
    const want = isTyped ? Number(text) || 0 : balance;
    const use = round2(Math.max(0, Math.min(balance, left, want)));

    left = round2(left - use);

    let warn = "";

    if (isTyped && want > balance) {
      warn = `เกินยอดคงเหลือ · ระบบใช้ได้สูงสุด ${formatComma(balance)} ฿`;
    } else if (isTyped && use < want) {
      warn = `ใช้ได้ ${formatComma(use)} ฿ ตามยอดที่ต้องชำระ`;
    }

    uses.push({ wallet, use, warn });
  }

  return uses;
};

/** ยอดที่ใช้จาก Wallet ทั้งหมด */
export const walletTotalOf = (uses: WalletUse[]) => {
  let total = 0;

  for (const use of uses) {
    total += use.use;
  }

  return round2(total);
};

// ---------- ช่องทางชำระ ----------

/** แถวของ BIL/master-account/ และ BIL/master-edc/ */
export type PayMaster = {
  id: number;
  code: string;
  name: string;
  active: boolean;
  account_no?: string;
};

export type PayLine = {
  kind: PayKind;
  amount: string;
  card: string[];
  edc: string;
  ref: string;
  account: string;
  cheque: string;
  date: string;
  coupon: string;
  /** ผลตรวจคูปองเงินสด (check-coupon-code) */
  couponCheck: "" | "ok" | "bad";
};

/** รายการชำระใหม่ของช่องทาง kind */
export const emptyPayLine = (kind: PayKind, amount: number): PayLine => ({
  kind,
  amount: amount ? String(amount) : "",
  card: ["", "", "", ""],
  edc: "",
  ref: "",
  account: "",
  cheque: "",
  date: "",
  coupon: "",
  couponCheck: "",
});

type ReceiptPayment = NonNullable<ReceiptByItemSerializer["payments"]>[number];

export type PayLineError = Partial<
  Record<"amount" | "card" | "edc" | "ref" | "account" | "cheque" | "date" | "coupon", boolean>
>;

/** ช่องทางที่ไม่ใช่เงินสด: ชื่อ และ payment type ของ receipt-by-item */
export const PAY_KINDS: { kind: PayKind; label: string; type: ReceiptPayment["type"] }[] = [
  { kind: "qr", label: "เงินโอน/QR Code", type: 2 },
  { kind: "cc", label: "บัตรเครดิต", type: 3 },
  { kind: "dc", label: "บัตรเดบิต", type: 9 },
  { kind: "chq", label: "แคชเชียร์เช็ค", type: 4 },
  { kind: "cpn", label: "คูปองเงินสด", type: 5 },
];

export const payLineErrorOf = (line: PayLine): PayLineError => {
  const error: PayLineError = {};

  if (!((Number(line.amount) || 0) > 0)) {
    error.amount = true;
  }

  if ((line.kind === "cc" || line.kind === "dc") && !/^\d{4}$/.test(line.card[3] || "")) {
    error.card = true;
  }

  if ((line.kind === "cc" || line.kind === "dc") && !line.edc) {
    error.edc = true;
  }

  if (line.kind === "qr" && !line.ref.trim()) {
    error.ref = true;
  }

  if (line.kind === "qr" && !line.account) {
    error.account = true;
  }

  if (line.kind === "chq" && !line.cheque.trim()) {
    error.cheque = true;
  }

  if (line.kind === "chq" && !line.date) {
    error.date = true;
  }

  if (line.kind === "cpn" && (!line.coupon.trim() || line.couponCheck !== "ok")) {
    error.coupon = true;
  }

  return error;
};

// ---------- ใบเสร็จ ----------

// payment type ของ receipt-by-item
const CASH_TYPE = 1;
const WALLET_TYPE = 6;

/** payments ของ receipt-by-item: เงินสด (รับ / ทอน), Wallet, แล้วช่องทางอื่นตามรายละเอียด */
export const receiptPaymentsOf = (summary: PaySummary, lines: PayLine[]) => {
  const payments: ReceiptPayment[] = [];
  const cashPaid = round2(Math.max(0, Math.min(summary.cash, summary.due - summary.others)));

  if (cashPaid > 0) {
    payments.push({
      type: CASH_TYPE,
      payment_no: `${summary.cash.toFixed(2)} / ${summary.change.toFixed(2)}`,
      payment_target: 0,
      value: cashPaid.toFixed(2),
    });
  }

  if (summary.walletTotal > 0) {
    payments.push({
      type: WALLET_TYPE,
      payment_no: "",
      payment_target: 0,
      value: summary.walletTotal.toFixed(2),
    });
  }

  for (const line of lines) {
    const kind = PAY_KINDS.find((item) => item.kind === line.kind) ?? PAY_KINDS[0];
    const isCard = line.kind === "cc" || line.kind === "dc";
    const numbers: Record<PayKind, string> = {
      qr: line.ref.trim(),
      cc: line.card.join(""),
      dc: line.card.join(""),
      chq: line.cheque.trim(),
      cpn: line.coupon.trim(),
    };

    payments.push({
      type: kind.type,
      payment_no: numbers[line.kind],
      payment_target: Number(isCard ? line.edc : line.account) || 0,
      value: (Number(line.amount) || 0).toFixed(2),
      ...(line.kind === "chq" && { cheque_date: line.date }),
    });
  }

  return payments;
};

/** ชื่อผู้รับบริการบนใบเสร็จ (patient_name_lang TH / EN) */
export const nameOptionsOf = (
  patient?: {
    full_name?: string;
    real_name_th?: string;
    real_name_en?: string;
  } | null,
) => {
  const options: { value: string; text: string }[] = [];

  if (patient?.real_name_th) {
    options.push({ value: "TH", text: patient.real_name_th });
  }

  if (patient?.real_name_en) {
    options.push({ value: "EN", text: patient.real_name_en });
  }

  if (options.length === 0) {
    options.push({ value: "TH", text: patient?.full_name || "" });
  }

  return options;
};

/** ข้อความหลังรับชำระ */
export const payFlashOf = (items: InvoiceItem[], due: number, receiptCode?: string) => {
  const pays = items.filter((item) => payStatusOf(item) === "pending").length;
  const packages = items.filter((item) => payStatusOf(item) === "package").length;
  const parts: string[] = [];

  if (pays > 0) {
    parts.push(
      `รับชำระ ${pays} รายการ ยอด ${formatComma(due)} บาท${receiptCode ? ` · ใบเสร็จ ${receiptCode}` : ""}`,
    );
  }

  if (packages > 0) {
    parts.push(`ตัดสิทธิ์ ${packages} รายการ`);
  }

  return parts.join(" | ");
};
